/**
 * Simple brand switcher - just switches asset paths and CSS files
 */

const BRAND_STORAGE_KEY = 'morgen-health:activeBrand';
const LEGACY_BRAND_STORAGE_KEY = 'activeBrand';
const DEFAULT_BRAND = '';

/** Brands that exist in public/assets and public/globals-*.css */
export const ALLOWED_BRANDS = ['', 'nuernberger', 'allianz'] as const;
export type AllowedBrand = (typeof ALLOWED_BRANDS)[number];

function isAllowedBrand(brand: string | null): brand is AllowedBrand {
  return brand !== null && (ALLOWED_BRANDS as readonly string[]).includes(brand);
}

function readStoredBrand(): string | null {
  return localStorage.getItem(BRAND_STORAGE_KEY) ?? localStorage.getItem(LEGACY_BRAND_STORAGE_KEY);
}

function persistBrand(brand: AllowedBrand): void {
  localStorage.setItem(BRAND_STORAGE_KEY, brand);
  localStorage.removeItem(LEGACY_BRAND_STORAGE_KEY);
}

function normalizeBrand(raw: string | null): AllowedBrand {
  if (isAllowedBrand(raw)) {
    return raw;
  }
  return DEFAULT_BRAND;
}

export function getActiveBrand(): AllowedBrand {
  const raw = readStoredBrand();
  const brand = normalizeBrand(raw);

  if (raw !== null && raw !== brand) {
    // Drop values from other apps (e.g. nordleasing) or stale keys
    persistBrand(brand);
  } else if (raw !== null && localStorage.getItem(LEGACY_BRAND_STORAGE_KEY) !== null) {
    // Migrate valid legacy key to namespaced storage
    persistBrand(brand);
  }

  return brand;
}

export function setActiveBrand(brand: string): void {
  const normalized = normalizeBrand(brand);
  persistBrand(normalized);
  updateCSS(normalized);
  window.dispatchEvent(new CustomEvent('brandChanged', { detail: { brand: normalized } }));
}

export function getAssetPath(path: string): string {
  const brand = getActiveBrand();
  if (!brand) return path;

  if (path.includes('/assets/identity/')) {
    return path.replace('/assets/identity/', `/assets/identity-${brand}/`);
  }

  return path;
}

function updateCSS(brand: AllowedBrand): void {
  const oldLink = document.getElementById('brand-css');
  if (oldLink) {
    oldLink.remove();
  }

  if (brand) {
    const link = document.createElement('link');
    link.id = 'brand-css';
    link.rel = 'stylesheet';
    link.href = `/globals-${brand}.css`;
    document.head.appendChild(link);
  }
}

if (typeof window !== 'undefined') {
  updateCSS(getActiveBrand());
}
